/**
 * The Arche Store's building blocks: an app's icon and its Open (or Play)
 * button, the lockup a shelf lists, shelves, charts, the featured hero,
 * Today's editorial cards, the category grid, the facts strip of an app page,
 * the store's own tabs, and its search field.
 *
 * Where the store looks like the Games page it uses the Games page's classes
 * (`fg-…`: rows, tiles, shelves, charts, the hero, icons, buttons), so the two
 * read as one store; what only the store has is `fs-…`.
 */
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { categoryName, genreName, useGamesSite } from '../context';
import { isNewGame } from '../format';
import { genreGradient } from '../genres';
import { SearchForm } from '../components/SearchForm';
import { SideScrollButtons, useSideScroll } from '../components/SideScroll';
import { appCount } from './format';
import { categoryGradient, categoryLook } from './looks';
import {
  STORE_APPS_PATH,
  STORE_CATEGORIES_PATH,
  STORE_GAMES_PATH,
  STORE_HOME_PATH,
  STORE_SEARCH_PATH,
  STORE_TOP_PATH,
  storeCategoryPath,
  storeStoryPath,
  type StoreTab,
} from './routes';
import type {
  StoreCard,
  StoreCategoryRef,
  StoreFeatured,
  StoreScreenshot,
  StoreTodayCardData,
} from './types';

export type StoreIconSize = 'sm' | 'md' | 'lg' | 'xl';
const PX: Record<StoreIconSize, number> = { sm: 48, md: 64, lg: 96, xl: 128 };

/** The gradient behind an item without art: its category's, or a game's genre's. */
function fallbackGradient(card: Pick<StoreCard, 'categories' | 'genres' | 'isGame'>): string {
  return card.isGame
    ? genreGradient(card.genres[0] ?? '')
    : categoryGradient(card.categories[0] ?? '');
}

/**
 * An app's icon, in the rounded-square app shape. An app without art gets its
 * first letter on its category's colours instead of an empty box.
 */
export function StoreIcon({
  card,
  size = 'md',
}: {
  card: Pick<StoreCard, 'title' | 'iconUrl' | 'iconUrlLarge' | 'categories' | 'genres' | 'isGame'>;
  size?: StoreIconSize;
}): ReactElement {
  const px = PX[size];
  const src = px > 64 ? (card.iconUrlLarge ?? card.iconUrl) : card.iconUrl;
  if (!src) {
    return (
      <span
        className={`fg-icon fg-icon--${size} fg-icon--letter`}
        style={{ backgroundImage: fallbackGradient(card) }}
        aria-hidden="true"
      >
        {card.title.trim().charAt(0).toUpperCase() || '?'}
      </span>
    );
  }
  return (
    <span className={`fg-icon fg-icon--${size}`} aria-hidden="true">
      <img src={src} alt="" width={px} height={px} loading="lazy" decoding="async" />
    </span>
  );
}

/**
 * Open (Play, for a game): opens the app. An app that needs an account sends
 * a signed-out visitor through sign-in first and back to it — the app's route
 * does that, so the link is the same for everyone and only the words change.
 * Nothing installs: there is no "Get".
 */
export function OpenButton({
  card,
  size = 'pill',
}: {
  card: Pick<StoreCard, 'title' | 'launchPath' | 'requiresSignIn' | 'isGame'>;
  size?: 'pill' | 'large';
}): ReactElement {
  const { Link, signedIn } = useGamesSite();
  const verb = card.isGame ? 'Play' : 'Open';
  const signInFirst = card.requiresSignIn && !signedIn;
  const label = size === 'large' && signInFirst ? `Sign in to ${verb.toLowerCase()}` : verb;
  return (
    <Link
      href={card.launchPath}
      className={`fg-play fg-play--${size}`}
      aria-label={
        signInFirst ? `Sign in to ${verb.toLowerCase()} ${card.title}` : `${verb} ${card.title}`
      }
    >
      {label}
    </Link>
  );
}

/**
 * The line under an item's name: an app's subtitle or primary category, a
 * game's genre — and, for something inside another arche, that arche.
 */
export function useCardSubtitle(card: StoreCard): string {
  const { genreLabels, categoryLabels } = useGamesSite();
  const own = card.isGame
    ? card.genres[0]
      ? genreName(genreLabels, card.genres[0])
      : 'Game'
    : (card.subtitle ??
      (card.categories[0] ? categoryName(categoryLabels, card.categories[0]) : 'App'));
  return card.kind === 'listing' ? `${own} · ${card.archeName}` : own;
}

/** One item on a line — the store's lockup: icon, name, its line, and Open. */
export function StoreRow({
  card,
  rank,
  iconSize = 'sm',
  headingLevel = 3,
}: {
  card: StoreCard;
  rank?: number;
  iconSize?: StoreIconSize;
  headingLevel?: 2 | 3 | 4;
}): ReactElement {
  const { Link, now } = useGamesSite();
  const subtitle = useCardSubtitle(card);
  const Heading = `h${headingLevel}` as const;
  return (
    <div className="fg-row">
      {rank !== undefined && (
        <span className="fg-row__rank" aria-label={`Number ${rank}`}>
          {rank}
        </span>
      )}
      <Link href={card.pagePath} className="fg-row__main">
        <StoreIcon card={card} size={iconSize} />
        <div className="fg-row__text">
          <Heading className="fg-row__title">{card.title}</Heading>
          <span className="fg-row__subtitle">
            {isNewGame(card.liveAt, now) && <span className="fg-badge">New</span>}
            {subtitle}
          </span>
        </div>
      </Link>
      <OpenButton card={card} />
    </div>
  );
}

/** One item standing up: a big icon over its name — the shelf card. */
export function StoreTile({ card }: { card: StoreCard }): ReactElement {
  const { Link } = useGamesSite();
  const subtitle = useCardSubtitle(card);
  return (
    <div className="fg-tile">
      <Link href={card.pagePath} className="fg-tile__main">
        <StoreIcon card={card} size="lg" />
        <span className="fg-tile__title">{card.title}</span>
        <span className="fg-tile__subtitle">{subtitle}</span>
      </Link>
      <OpenButton card={card} />
    </div>
  );
}

/**
 * A titled shelf that scrolls sideways: `rows` stacks items three to a column,
 * `tiles` stands them up one by one. Nothing renders for an empty shelf.
 */
export function StoreShelf({
  id,
  title,
  cards,
  seeAllHref,
  seeAllLabel,
  layout = 'rows',
}: {
  id: string;
  title: string;
  cards: StoreCard[];
  seeAllHref?: string;
  seeAllLabel?: string;
  layout?: 'rows' | 'tiles';
}): ReactElement | null {
  const { Link } = useGamesSite();
  const { ref, room, page } = useSideScroll(cards.length);
  if (cards.length === 0) return null;
  const headingId = `fs-shelf-${id}`;
  const listId = `${headingId}-list`;
  return (
    <section className="fg-shelf" aria-labelledby={headingId}>
      <div className="fg-shelf__head">
        <h2 id={headingId} className="fg-h2">
          {title}
        </h2>
        <div className="fg-shelf__tools">
          {seeAllHref && (
            <Link href={seeAllHref} className="fg-see-all" aria-label={seeAllLabel}>
              See all
            </Link>
          )}
          <SideScrollButtons controls={listId} label={title} room={room} onPage={page} />
        </div>
      </div>
      <ul ref={ref} id={listId} className={`fg-shelf__list fg-shelf__list--${layout}`}>
        {cards.map((card) => (
          <li key={card.ref}>
            {layout === 'rows' ? <StoreRow card={card} /> : <StoreTile card={card} />}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A ranked list: the number is the item's place. "See all" goes on past `limit`. */
export function StoreChart({
  id,
  title,
  cards,
  limit = 12,
  seeAllHref,
  seeAllLabel,
}: {
  id: string;
  title: string;
  cards: StoreCard[];
  limit?: number;
  seeAllHref?: string;
  seeAllLabel?: string;
}): ReactElement | null {
  const { Link } = useGamesSite();
  if (cards.length === 0) return null;
  const headingId = `fs-chart-${id}`;
  return (
    <section className="fg-shelf" aria-labelledby={headingId}>
      <div className="fg-shelf__head">
        <h2 id={headingId} className="fg-h2">
          {title}
        </h2>
        {seeAllHref && (
          <Link href={seeAllHref} className="fg-see-all" aria-label={seeAllLabel}>
            See all
          </Link>
        )}
      </div>
      <ol className="fg-chart">
        {cards.slice(0, limit).map((card, i) => (
          <li key={card.ref}>
            <StoreRow card={card} rank={i + 1} />
          </li>
        ))}
      </ol>
    </section>
  );
}

/** The art behind a big card: hero art when there is some, else the item's own icon, blurred. */
function CardArt({
  heroUrl,
  heroUrlSmall,
  card,
  eager,
}: {
  heroUrl: string | null;
  heroUrlSmall: string | null;
  card: StoreCard | null;
  eager: boolean;
}): ReactElement {
  if (heroUrl && heroUrlSmall) {
    return (
      <img
        className="fg-hero__picture"
        src={heroUrlSmall}
        srcSet={`${heroUrlSmall} 800w, ${heroUrl} 1600w`}
        sizes="(min-width: 860px) 50vw, 100vw"
        alt=""
        loading={eager ? undefined : 'lazy'}
      />
    );
  }
  const art = card ? (card.iconUrlLarge ?? card.iconUrl) : null;
  const glow: CSSProperties = art
    ? { backgroundImage: `url("${encodeURI(art)}")` }
    : { backgroundImage: card ? fallbackGradient(card) : categoryGradient('') };
  return (
    <>
      <span className="fg-hero__glow" style={glow} aria-hidden="true" />
      {card && (
        <div className="fg-hero__art" aria-hidden="true">
          <StoreIcon card={card} size="xl" />
        </div>
      )}
    </>
  );
}

/** The featured apps, one big card each, swiped through (CSS scroll snapping). */
export function StoreHero({
  featured,
  title = 'Featured apps',
}: {
  featured: StoreFeatured[];
  title?: string;
}): ReactElement | null {
  const { Link, categoryLabels } = useGamesSite();
  const { ref, room, page } = useSideScroll(featured.length);
  if (featured.length === 0) return null;
  return (
    <section className="fg-hero-shelf" aria-labelledby="fs-featured">
      <h2 id="fs-featured" className="fg-visually-hidden">
        {title}
      </h2>
      <SideScrollButtons
        controls="fs-featured-list"
        label={title.toLowerCase()}
        room={room}
        onPage={page}
        overlay
      />
      <ul ref={ref} id="fs-featured-list" className="fg-hero-list">
        {featured.map(({ card, label, tagline, heroUrl, heroUrlSmall }, index) => (
          <li key={card.ref} className="fg-hero">
            <CardArt
              heroUrl={heroUrl}
              heroUrlSmall={heroUrlSmall}
              card={card}
              eager={index === 0}
            />
            <span className="fg-hero__shade" aria-hidden="true" />
            <div className="fg-hero__copy">
              <p className="fg-kicker fg-kicker--on-art">
                {label ??
                  (card.categories[0]
                    ? categoryName(categoryLabels, card.categories[0])
                    : 'Featured')}
              </p>
              <h3 className="fg-hero__title">
                <Link href={card.pagePath} className="fg-stretch">
                  {card.title}
                </Link>
              </h3>
              <p className="fg-hero__tagline">{tagline ?? card.subtitle ?? card.description}</p>
            </div>
            <div className="fg-hero__foot">
              <StoreIcon card={card} size="sm" />
              <span className="fg-hero__name">
                <span className="fg-hero__name-title">{card.title}</span>
                {card.kind === 'listing' && (
                  <span className="fg-hero__name-sub">in {card.archeName}</span>
                )}
              </span>
              <OpenButton card={card} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One card on Today, in the store's editorial style: a story (art, kicker,
 * title, tagline — it opens the story), a collection (its first apps listed
 * on the card), or a daily pick (today's app, big).
 */
export function TodayCard({
  card,
  eager = false,
}: {
  card: StoreTodayCardData;
  eager?: boolean;
}): ReactElement {
  const { Link } = useGamesSite();
  const { collection } = card;
  if (card.kind === 'daily') {
    const pick = card.pick.card;
    return (
      <article className="fs-today fs-today--daily">
        <div className="fs-today__art">
          <CardArt
            heroUrl={collection.heroUrl}
            heroUrlSmall={collection.heroUrlSmall}
            card={pick}
            eager={eager}
          />
          <span className="fg-hero__shade" aria-hidden="true" />
          <div className="fs-today__copy">
            <p className="fg-kicker fg-kicker--on-art">{collection.kicker ?? collection.title}</p>
            <h3 className="fs-today__title">
              <Link href={pick.pagePath} className="fg-stretch">
                {pick.title}
              </Link>
            </h3>
            <p className="fs-today__tagline">
              {card.pick.blurb ?? pick.subtitle ?? pick.description}
            </p>
          </div>
        </div>
        <div className="fg-hero__foot fs-today__foot">
          <StoreIcon card={pick} size="sm" />
          <span className="fg-hero__name">
            <span className="fg-hero__name-title">{pick.title}</span>
          </span>
          <OpenButton card={pick} />
        </div>
      </article>
    );
  }
  const first = collection.items[0]?.card ?? null;
  return (
    <article className={`fs-today fs-today--${card.kind}`}>
      <div className="fs-today__art">
        <CardArt
          heroUrl={collection.heroUrl}
          heroUrlSmall={collection.heroUrlSmall}
          card={first}
          eager={eager}
        />
        <span className="fg-hero__shade" aria-hidden="true" />
        <div className="fs-today__copy">
          {collection.kicker && <p className="fg-kicker fg-kicker--on-art">{collection.kicker}</p>}
          <h3 className="fs-today__title">
            <Link href={storeStoryPath(collection.slug)} className="fg-stretch">
              {collection.title}
            </Link>
          </h3>
          {collection.tagline && <p className="fs-today__tagline">{collection.tagline}</p>}
        </div>
      </div>
      {card.kind === 'collection' && collection.items.length > 0 && (
        <ul className="fs-today__list">
          {collection.items.slice(0, 4).map(({ card: item }) => (
            <li key={item.ref}>
              <StoreRow card={item} headingLevel={4} />
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

/** Every category with apps, as a coloured tile that opens it. */
export function StoreCategoryGrid({
  categories,
  title = 'Browse by category',
  headingId = 'fs-categories',
}: {
  categories: StoreCategoryRef[];
  title?: string;
  headingId?: string;
}): ReactElement | null {
  const { Link } = useGamesSite();
  const shown = categories.filter((c) => (c.count ?? 0) > 0);
  if (shown.length === 0) return null;
  return (
    <section className="fg-shelf" aria-labelledby={headingId}>
      <div className="fg-shelf__head">
        <h2 id={headingId} className="fg-h2">
          {title}
        </h2>
      </div>
      <ul className="fg-genres">
        {shown.map((category) => (
          <li key={category.id}>
            <Link
              href={storeCategoryPath(category.id)}
              className="fg-genre"
              aria-label={`${category.label}, ${appCount(category.count ?? 0)}`}
            >
              <span
                className="fg-genre__fill"
                style={{ backgroundImage: categoryGradient(category.id) }}
                aria-hidden="true"
              />
              <span className="fg-genre__glyph" aria-hidden="true">
                {categoryLook(category.id).glyph}
              </span>
              <span className="fg-genre__label" aria-hidden="true">
                {category.label}
              </span>
              <span className="fg-genre__count" aria-hidden="true">
                {appCount(category.count ?? 0)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A category page's banner: its colours, its glyph oversized, its name and count. */
export function CategoryBanner({
  category,
  total,
}: {
  category: StoreCategoryRef;
  total: number;
}): ReactElement {
  return (
    <header className="fg-banner" style={{ backgroundImage: categoryGradient(category.id) }}>
      <span className="fg-banner__glyph" aria-hidden="true">
        {categoryLook(category.id).glyph}
      </span>
      <h1 className="fg-banner__title">{category.label}</h1>
      <p className="fg-banner__count">{appCount(total)}</p>
    </header>
  );
}

/** One fact on an app page's strip: a small label over its value. */
export interface InfoFact {
  label: string;
  value: ReactNode;
  detail?: string;
}

/** The facts strip under an app's name: category and rank, publisher, price, where it runs. */
export function InfoStrip({ facts }: { facts: InfoFact[] }): ReactElement {
  return (
    <dl className="fs-info">
      {facts.map((fact) => (
        <div key={fact.label} className="fs-info__fact">
          <dt className="fs-info__label">{fact.label}</dt>
          <dd className="fs-info__value">{fact.value}</dd>
          {fact.detail && <dd className="fs-info__detail">{fact.detail}</dd>}
        </div>
      ))}
    </dl>
  );
}

const TABS: ReadonlyArray<{ tab: StoreTab; label: string; href: string; games?: boolean }> = [
  { tab: 'today', label: 'Today', href: STORE_HOME_PATH },
  { tab: 'apps', label: 'Apps', href: STORE_APPS_PATH },
  { tab: 'games', label: 'Games', href: STORE_GAMES_PATH, games: true },
  { tab: 'top', label: 'Top Charts', href: STORE_TOP_PATH },
  { tab: 'categories', label: 'Categories', href: STORE_CATEGORIES_PATH },
  { tab: 'search', label: 'Search', href: STORE_SEARCH_PATH },
];

/**
 * The store's own tabs: a rail down the side on a wide screen, a strip along
 * the top on a narrow one (the same list, laid out by CSS). Games appears only
 * while the Games page is shown.
 */
export function StoreNav({
  current,
  gamesShown,
}: {
  current: StoreTab;
  gamesShown: boolean;
}): ReactElement {
  const { Link } = useGamesSite();
  return (
    <nav aria-label="Arche Store" className="fs-nav">
      <p className="fs-nav__brand" aria-hidden="true">
        Arche Store
      </p>
      <ul className="fs-nav__list">
        {TABS.filter((t) => gamesShown || !t.games).map((t) => (
          <li key={t.tab}>
            <Link
              href={t.href}
              className={`fs-nav__link${t.tab === current ? ' fs-nav__link--on' : ''}`}
              aria-current={t.tab === current ? 'page' : undefined}
            >
              {t.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * The store's search field — the Games page's field, aimed at the store's
 * search page: a real GET form without script; the app passes `value`,
 * `onChange` and `onSubmit` to search as the visitor types.
 */
export function StoreSearchForm(
  props: Omit<Parameters<typeof SearchForm>[0], 'action' | 'label' | 'placeholder'>,
): ReactElement {
  return (
    <SearchForm
      id="fs-search"
      {...props}
      action={STORE_SEARCH_PATH}
      label="Search the Arche Store"
      placeholder="Apps, games and categories"
    />
  );
}

/** The longer side a screenshot is served at in `url` (and half that in `urlSmall`). */
const SHOT_LARGE = 1280;

/**
 * An app's screenshots: a fixed-height strip that scrolls sideways, snapping
 * to each shot, with page buttons beside the heading. Portrait shots sit about
 * three across on a desktop, landscape ones about one and a half. Only the
 * first loads eagerly; every image carries its size, so nothing shifts.
 */
export function StoreScreenshots({
  shots,
  appTitle,
}: {
  shots: StoreScreenshot[];
  appTitle: string;
}): ReactElement | null {
  const { ref, room, page } = useSideScroll(shots.length);
  if (shots.length === 0) return null;
  const listId = 'fs-shots-list';
  return (
    <section className="fs-shots" aria-labelledby="fs-shots-title">
      <div className="fs-shots__head">
        <h2 id="fs-shots-title" className="fg-h2">
          Screenshots
        </h2>
        <SideScrollButtons
          controls={listId}
          label={`${appTitle} screenshots`}
          room={room}
          onPage={page}
        />
      </div>
      <ul ref={ref} id={listId} className="fs-shots__list">
        {shots.map((shot, i) => {
          const portrait = shot.height > shot.width;
          // The served copies keep the shape; scale the stored size to the large one.
          const scale = SHOT_LARGE / Math.max(shot.width, shot.height, 1);
          const width = Math.max(1, Math.round(shot.width * Math.min(scale, 1)));
          const height = Math.max(1, Math.round(shot.height * Math.min(scale, 1)));
          return (
            <li
              key={shot.url}
              className={`fs-shot ${portrait ? 'fs-shot--portrait' : 'fs-shot--landscape'}`}
            >
              <img
                src={shot.url}
                srcSet={`${shot.urlSmall} ${Math.round(width / 2)}w, ${shot.url} ${width}w`}
                sizes={
                  portrait ? '(max-width: 640px) 60vw, 280px' : '(max-width: 640px) 85vw, 620px'
                }
                width={width}
                height={height}
                alt={shot.alt ?? `${appTitle}, screenshot ${i + 1} of ${shots.length}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
